// src/pages/HomePage.js

import React, {
  useContext,
  useState,
  useEffect,
  useMemo
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { usePets } from '../hooks/rq/usePets';
import PetCard from './PetCard';
import Spinner from '../components/Spinner';
import { FilterContext } from '../context/FilterContext';
import { AuthContext } from '../context/AuthContext';
import { LocationContext } from '../context/LocationContext';

/**
 * HomePage migrada a React Query con scroll virtualizado.
 * Se mantiene la gestión de scroll y restauración de posición (homeKey).
 */
export default function HomePage() {
  // 1) Contextos y estado de padding
  const { filters }         = useContext(FilterContext);
  const { isAuthenticated } = useContext(AuthContext);
  const { coords }          = useContext(LocationContext);
  const [paddingTop, setPaddingTop] = useState(60);

  // Clave para guardar/restaurar scroll
  const homeKey = '/';

  // 2) React Query: fetch infinito de mascotas
  const species         = filters.species;
  const sex             = filters.sex;
  const filtersLocation = filters.location;

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = usePets({ species, sex, filtersLocation, coords, isAuthenticated });

  // Combinar y deduplicar mascotas por ID
  const pets = useMemo(() => {
    const all = data?.pages.flatMap(page => page.results) ?? [];
    const map = new Map();
    all.forEach(p => map.set(p.id, p));
    return Array.from(map.values());
  }, [data]);

  // ─── Hook local: detecta si estamos en móvil (ancho < 768px)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 3) Configurar virtualizador
  const rowVirtualizer = useVirtualizer({
    count: pets.length,
    // Usamos el contenedor con clase 'home-container' como scrollElement
    getScrollElement: () => document.querySelector('.home-container'),
    // Ahora elegimos tamaño según isMobile
    estimateSize: () => isMobile ? 550 : 770,  // Altura aproximada de cada PetCard (ajústala si quieres)
    overscan: 10,             // Renderizar 5 elementos extra como buffer
  });
  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize    = rowVirtualizer.getTotalSize();

  // 4) Infinite scroll (mantenemos tu lógica existente)
  useEffect(() => {
    const container = document.querySelector('.home-container');
    const handler = () => {
      const scrollTop = container ? container.scrollTop : window.scrollY;
      const scrollH   = container ? container.scrollHeight : document.documentElement.scrollHeight;
      const clientH   = container ? container.clientHeight : window.innerHeight;
      if (scrollTop + clientH >= scrollH - 400 && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    };
    (container || window).addEventListener('scroll', handler);
    return () => (container || window).removeEventListener('scroll', handler);
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // 5) Guardar/restaurar posición del scroll
  useEffect(() => {
    const handleScrollSave = () => {
      sessionStorage.setItem(homeKey, window.scrollY);
    };
    window.addEventListener('scroll', handleScrollSave);
    return () => window.removeEventListener('scroll', handleScrollSave);
  }, [homeKey]);

  useEffect(() => {
    if (pets.length > 0) {
      const saved = sessionStorage.getItem(homeKey);
      if (saved) {
        setTimeout(() => window.scrollTo(0, Number(saved)), 300);
      }
    }
  }, [pets, homeKey, isAuthenticated]);

  // 6) Ajustar paddingTop según scroll
  useEffect(() => {
    const handlePadding = () => {
      const scrollY   = window.scrollY;
      const threshold = 100;
      const newPadding = Math.max(60 - (60 * (scrollY / threshold)), 0);
      setPaddingTop(newPadding);
    };
    window.addEventListener('scroll', handlePadding);
    return () => window.removeEventListener('scroll', handlePadding);
  }, []);

  // 7) Renderizado
  if (isError) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <p>Error al cargar mascotas.</p>
        <pre style={{ color: 'red' }}>{error.message}</pre>
        <button className="btn" onClick={refetch}>Reintentar</button>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: `${paddingTop}px` }}>
      {/* Contenedor virtualizado */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: `${totalSize}px`
        }}
      >
        {virtualItems.map(({ index, start }) => {
          const pet = pets[index];
          if (!pet) return null;
          return (
            <div
              key={pet.id}
              style={{
                position: 'absolute',
                top: `${start}px`,
                width: '100%',
              }}
            >
              <PetCard pet={pet} />
            </div>
          );
        })}
      </div>

      {/* Spinner de carga */}
      {(isLoading || isFetchingNextPage) && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100vh'
        }}>
          <Spinner
            spinnerText={isLoading ? 'Cargando' : ''}
            size={222}
            showEllipses={true}
            pulseScale={1.008}
            pulseDuration={1}
            textCycleDuration={1.6}
            textColor="black"
            textSize="0.7rem"
            textOffsetX="-12px"
            textOffsetY="20px"
            containerOffsetY="-50%"
          />
        </div>
      )}

      {/* Mensaje final */}
      {!hasNextPage && !isLoading && !isFetchingNextPage && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>No hay más mascotas para mostrar.</p>
        </div>
      )}
    </div>
  );
}
